import React from 'react';
import { CartesianGrid, Line, LineChart, ReferenceArea, Tooltip, XAxis, YAxis } from 'recharts';
import { fmt, fmtCount, fmtK } from '../../utils/formatters';
import { CONDITION_NAMES, HourlyProfile } from '../../utils/weather';
import { TD, TD_NUM, TH, TH_NUM, TIP_STYLE, TableToggle, useWidth } from './weatherUi';

// By the hour: median revenue per local hour on ordinary days (ink-soft) and
// on condition days (plum), 6am to 8pm, with the 6 to 11 rush shaded. For a
// café the story is usually "rain takes the rush and the afternoon is fine".

const FIRST = 6;
const LAST = 20;
const RUSH_END = 11;
const ORDINARY = '#6B6670';
const PLUM = '#4A2D5E';

/** 6 → "6am", 12 → "12pm", 13 → "1pm". */
export const hourLabel = (h: number): string => `${fmtCount(h % 12 === 0 ? 12 : h % 12)}${h < 12 ? 'am' : 'pm'}`;

/** Condition minus ordinary over a run of hours, in dollars. */
const gap = (p: HourlyProfile, from: number, to: number) => {
  let sum = 0;
  for (let h = from; h < to; h += 1) sum += (p.condition[h] ?? 0) - (p.ordinary[h] ?? 0);
  return sum;
};
const lessMore = (v: number) => (Math.round(v) === 0 ? 'about the same' : `${fmt(Math.abs(v))} ${v < 0 ? 'less' : 'more'}`);

export function hourlySentence(condition: string, p: HourlyProfile): string {
  const name = CONDITION_NAMES[condition] ?? condition;
  return `${name} against ordinary days, by the hour: the ${fmtCount(FIRST)} to ${hourLabel(RUSH_END)} rush took in `
    + `${lessMore(gap(p, FIRST, RUSH_END))}, and ${hourLabel(RUSH_END)} to ${hourLabel(LAST)} ${lessMore(gap(p, RUSH_END, LAST + 1))}.`;
}

export default function HourlyChart({ condition, hourly }: { condition: string; hourly: HourlyProfile }) {
  const [ref, width] = useWidth<HTMLDivElement>(320);
  const name = CONDITION_NAMES[condition] ?? condition;
  const rows = Array.from({ length: LAST - FIRST + 1 }, (_, i) => {
    const hour = FIRST + i;
    return { hour, ordinary: hourly.ordinary[hour] ?? 0, condition: hourly.condition[hour] ?? 0 };
  });
  return (
    <div data-hourly>
      <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 font-sans text-[10px] font-medium text-[#6B6670]">
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-0.5 w-4 rounded-full bg-[#4A2D5E]" />{name}</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-0.5 w-4 rounded-full bg-[#6B6670]" />Ordinary days</span>
        <span className="inline-flex items-center gap-1.5"><span aria-hidden="true" className="h-2.5 w-3 rounded-[2px] bg-[#F0EBF5]" />Morning rush, 6 to 11</span>
      </div>
      <div ref={ref} role="img" aria-label={hourlySentence(condition, hourly)} className="w-full min-w-0 overflow-hidden">
        <LineChart width={width} height={200} data={rows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke="#E6E2DE" />
          <ReferenceArea x1={FIRST} x2={RUSH_END} fill="#F0EBF5" fillOpacity={1} stroke="none" ifOverflow="hidden" />
          <XAxis
            dataKey="hour" type="number" domain={[FIRST, LAST]} ticks={[6, 8, 10, 12, 14, 16, 18, 20]}
            tickFormatter={hourLabel} tick={{ fontSize: 10, fill: '#6B6670', fontFamily: 'DM Sans' }}
            axisLine={{ stroke: '#E6E2DE' }} tickLine={false}
          />
          <YAxis
            width={44} tickFormatter={fmtK} tick={{ fontSize: 10, fill: '#6B6670', fontFamily: 'DM Sans' }}
            axisLine={false} tickLine={false}
          />
          <Tooltip
            contentStyle={TIP_STYLE}
            labelFormatter={(h) => hourLabel(Number(h))}
            formatter={(v: number, key: string) => [fmt(v), key === 'condition' ? name : 'Ordinary days']}
          />
          <Line dataKey="ordinary" type="monotone" stroke={ORDINARY} strokeWidth={2} dot={false} isAnimationActive={false}
            activeDot={{ r: 4, stroke: '#FFFFFF', strokeWidth: 2, fill: ORDINARY }} />
          <Line dataKey="condition" type="monotone" stroke={PLUM} strokeWidth={2} dot={false} isAnimationActive={false}
            activeDot={{ r: 4, stroke: '#FFFFFF', strokeWidth: 2, fill: PLUM }} />
        </LineChart>
      </div>
      <p className="mt-1 font-sans text-[11px] text-[#6B6670]">
        Median sales per hour across {fmtCount(hourly.n_condition)} {name.toLowerCase()} and {fmtCount(hourly.n_ordinary)} ordinary days.
      </p>
      <TableToggle className="mt-2">
        <table className="w-full font-sans text-xs">
          <caption className="sr-only">{name} against ordinary days, median sales by hour</caption>
          <thead>
            <tr className="border-b border-[#E6E2DE]">
              <th scope="col" className={TH}>Hour</th>
              <th scope="col" className={TH_NUM}>Ordinary days</th>
              <th scope="col" className={TH_NUM}>{name}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E6E2DE]">
            {rows.map((r) => (
              <tr key={r.hour}>
                <td className={TD}>{hourLabel(r.hour)}</td>
                <td className={TD_NUM}>{fmt(r.ordinary)}</td>
                <td className={TD_NUM}>{fmt(r.condition)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableToggle>
    </div>
  );
}
